import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';

const TermsAndConditions = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ maxWidth: '1000px', mx: 'auto', pb: 6, px: { xs: 1, sm: 2 } }}>
      {/* Back Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/dashboard')}
          sx={{
            color: '#4CA1AF',
            textTransform: 'none',
            '&:hover': { background: 'rgba(76, 161, 175, 0.1)' },
          }}
        >
          Back to Dashboard
        </Button>

        <Button
          endIcon={<OpenInNewIcon />}
          component="a"
          href="https://glocalprayer.net/terms-and-conditions/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: '#6DD5FA',
            textTransform: 'none',
            fontSize: '0.85rem',
            '&:hover': { background: 'rgba(109, 213, 250, 0.1)' },
          }}
        >
          View on glocalprayer.net
        </Button>
      </Box>

      {/* Header Paper */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(44, 62, 80, 0.95) 0%, rgba(26, 31, 46, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(76, 161, 175, 0.3)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: 'rgba(76, 161, 175, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DescriptionIcon sx={{ color: '#4CA1AF', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: { xs: '1.5rem', sm: '1.85rem' } }}>
              Terms & Conditions
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              Last updated: January 2026 • Glocal Prayer Network (GPN)
            </Typography>
          </Box>
        </Box>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.95rem', lineHeight: 1.6, mt: 1 }}>
          Welcome to the Glocal Prayer Network (GPN) Admin Portal. By accessing or using this administration console, mobile applications, and network services, you agree to comply with and be bound by the following Terms & Conditions.
        </Typography>
      </Paper>

      {/* Content Paper */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          background: 'rgba(30, 41, 59, 0.85)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          color: 'rgba(255, 255, 255, 0.85)',
          lineHeight: 1.7,
        }}
      >
        <Typography variant="h6" sx={{ color: '#4CA1AF', fontWeight: 700, mb: 1.5 }}>
          1. Acceptance of Terms
        </Typography>
        <Typography paragraph sx={{ fontSize: '0.95rem' }}>
          By creating an administrative account or managing any organization, prayer circle, event, or community resource on the Glocal Prayer Network (GPN), you agree to these Terms and Conditions in full. If you disagree with any part of these terms, you must not use our administration portal or services.
        </Typography>

        <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        <Typography variant="h6" sx={{ color: '#4CA1AF', fontWeight: 700, mb: 1.5 }}>
          2. Administration Portal Stewardship & Eligibility
        </Typography>
        <Typography paragraph sx={{ fontSize: '0.95rem' }}>
          Administrative access is granted exclusively to authorized pastors, ministry staff, church leadership representatives, and moderators of participating Christian ministries and organizations. Administrators must provide accurate, current, and complete organizational information during registration and maintain its accuracy.
        </Typography>

        <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        <Typography variant="h6" sx={{ color: '#4CA1AF', fontWeight: 700, mb: 1.5 }}>
          3. Sacred Prayer Content & Community Moderation
        </Typography>
        <Typography paragraph sx={{ fontSize: '0.95rem' }}>
          GPN is a Christ-centered platform dedicated to prayer, spiritual upliftment, and Christian fellowship. Administrators are expected to uphold these principles when moderating user contributions:
        </Typography>
        <List sx={{ pl: 2, listStyleType: 'disc' }}>
          <ListItem sx={{ display: 'list-item', py: 0.5 }}>
            <ListItemText primary="Prayer Requests: Prayers may be public or private. Private requests must be handled with utmost confidentiality and never disseminated outside authorized prayer teams." />
          </ListItem>
          <ListItem sx={{ display: 'list-item', py: 0.5 }}>
            <ListItemText primary="Testimonies & Devotionals: Testimonies must be genuine, edifying, and focused on God's grace. Content must not contain defamatory, obscene, or fraudulent claims." />
          </ListItem>
          <ListItem sx={{ display: 'list-item', py: 0.5 }}>
            <ListItemText primary="Prohibited Content: Hate speech, political propaganda, commercial solicitation, harassment, or theological divisiveness contrary to biblical love are strictly prohibited." />
          </ListItem>
        </List>

        <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        <Typography variant="h6" sx={{ color: '#4CA1AF', fontWeight: 700, mb: 1.5 }}>
          4. Share & Accept and Ride Connect Disclaimers
        </Typography>
        <Typography paragraph sx={{ fontSize: '0.95rem' }}>
          The GPN platform facilitates mutual assistance and church transportation as a convenience for fellowship members. GPN does not inspect, warrant, or assume liability for shared physical items, nor does GPN provide commercial transportation services. Church administrators are responsible for guiding their members to exercise due diligence and mutual respect.
        </Typography>

        <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        <Typography variant="h6" sx={{ color: '#4CA1AF', fontWeight: 700, mb: 1.5 }}>
          5. Intellectual Property
        </Typography>
        <Typography paragraph sx={{ fontSize: '0.95rem' }}>
          All trademarks, service marks, logo designs, graphics, and software underlying the Glocal Prayer Network are the property of GPN and its licensors. Organizations retain ownership of their proprietary logos and localized materials uploaded to their organization branch.
        </Typography>

        <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        <Typography variant="h6" sx={{ color: '#4CA1AF', fontWeight: 700, mb: 1.5 }}>
          6. Limitation of Liability & Termination
        </Typography>
        <Typography paragraph sx={{ fontSize: '0.95rem' }}>
          GPN provides its administrative tools on an "as is" and "as available" basis. GPN reserves the right to suspend or terminate administrative privileges for violation of these Terms, breach of confidentiality, or actions detrimental to the peace and safety of the community.
        </Typography>

        <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        <Typography variant="h6" sx={{ color: '#4CA1AF', fontWeight: 700, mb: 1.5 }}>
          7. Inquiries & Contact
        </Typography>
        <Typography sx={{ fontSize: '0.95rem' }}>
          For inquiries concerning these Terms & Conditions, please contact the GPN Legal & Ministry Support team at{' '}
          <a href="mailto:support@glocalprayer.net" style={{ color: '#4CA1AF', textDecoration: 'underline' }}>
            support@glocalprayer.net
          </a>{' '}
          or visit{' '}
          <a href="https://glocalprayer.net/contact-us/" target="_blank" rel="noopener noreferrer" style={{ color: '#4CA1AF', textDecoration: 'underline' }}>
            glocalprayer.net/contact-us
          </a>.
        </Typography>
      </Paper>
    </Box>
  );
};

export default TermsAndConditions;
